/**
 * verdictDraft — PURE, LOCAL draft helpers for the L1A recommendation flow
 * (Pass 2 / 2.2).
 *
 * WHY LOCAL (not a network draft): the portal has NO AI-draft endpoint of its
 * own — the only `autoDraft*` plumbing lives in the `functions` repo
 * (`l1aTemplate.js::autoDraftVerdict`), which is a SEPARATE deploy on an
 * unrelated branch and OUT OF SCOPE here. Per the Pass-2 brief, when no
 * portal-side draft util exists we implement the draft as a thin local helper
 * (NOT a fabricated network endpoint). These deterministic helpers seed an
 * EDITABLE draft staff then refine; the cross-candidate synthesis that the
 * deployed report renders stays with the functions repo (see the
 * `TODO(pass2-functions)` hand-off in the Verdict tab).
 *
 * Both functions are PURE (no React, no Firebase) → unit-tested in
 * `test/verdictDraft.test.cjs`.
 */
import type { VerdictStatus } from './staffIntake';

/** The evaluation signal for ONE candidate the per-property draft reads. */
export interface PropertyEvalSummary {
  /** Display label (already resolved, e.g. "Property A — 12 Oak St"). */
  label: string;
  /** ≥1 floor carries a closed/grid-able boundary. */
  hasGrid: boolean;
  /** ≥1 entrance (door) marked. */
  hasEntrance: boolean;
  /** AUO fixtures all resolved (or none to resolve). */
  auoResolved: boolean;
  /** number of floors evaluated. */
  floorCount: number;
}

/**
 * PURE — seed a per-property recommendation from its evaluation completeness.
 * Conservative: a property is only "recommended" outright once it is fully
 * evaluated (grid + entrance + AUO); a gridded-but-unresolved property drafts as
 * "recommended_with_remedies" (work remains); an un-gridded one drafts as ''
 * (no call yet — there is nothing to recommend on). Staff EDIT the result.
 */
export function draftPropertyRecommendation(s: PropertyEvalSummary): {
  status: VerdictStatus;
  reasoning: string;
} {
  const label = (s.label || 'This property').trim() || 'This property';
  if (!s.hasGrid) {
    return {
      status: '',
      reasoning:
        `${label} has no traced grid yet — trace the boundary (and mark an entrance) ` +
        `before drafting a recommendation.`,
    };
  }
  if (!s.hasEntrance) {
    return {
      status: 'recommended_with_remedies',
      reasoning:
        `${label}: grid traced across ${s.floorCount} floor${s.floorCount === 1 ? '' : 's'}, ` +
        `but no principal entrance is marked yet. Mark the entrance, then confirm the call.`,
    };
  }
  if (!s.auoResolved) {
    return {
      status: 'recommended_with_remedies',
      reasoning:
        `${label}: grid + entrance evaluated; AUO zones are still being resolved. ` +
        `Draft = recommended with remedies pending the AUO review.`,
    };
  }
  return {
    status: 'recommended',
    reasoning:
      `${label}: fully evaluated — grid, entrance and AUO zones all resolved across ` +
      `${s.floorCount} floor${s.floorCount === 1 ? '' : 's'}. Draft = recommended.`,
  };
}

/** One candidate as the cross-property "Summarize" reads it. */
export interface CandidateVerdictSummary {
  label: string;
  /** the per-property recommendation status. */
  status: VerdictStatus;
  reasoning?: string;
  /** is this the §D recommended winner? */
  isWinner: boolean;
  /** grid + entrance ready (proceed-able into the comparison). */
  ready: boolean;
}

const STATUS_PHRASE: Record<Exclude<VerdictStatus, ''>, string> = {
  recommended: 'recommended',
  recommended_with_remedies: 'recommended with remedies',
  not_recommended: 'not recommended',
};

/**
 * PURE — synthesise the project-level §D verdict TEXT from the per-property
 * recommendations + the winner. One headline line naming the winner, then one
 * line per candidate with its per-property call. Blank input → '' (the caller
 * keeps the "to be completed at curation" placeholder). Staff EDIT the result.
 */
export function summarizeVerdict(cands: CandidateVerdictSummary[]): string {
  const list = (cands || []).filter((c) => (c.label || '').trim());
  if (list.length === 0) return '';

  const winner = list.find((c) => c.isWinner) || null;
  const readyCount = list.filter((c) => c.ready).length;

  const lines: string[] = [];
  if (winner) {
    const ph = winner.status ? STATUS_PHRASE[winner.status as Exclude<VerdictStatus, ''>] : '';
    lines.push(
      `Across ${list.length} candidate${list.length === 1 ? '' : 's'}, ` +
        `${winner.label.trim()} is the recommended property${ph ? ` (${ph})` : ''}.`,
    );
  } else {
    lines.push(
      `Across ${list.length} candidate${list.length === 1 ? '' : 's'} ` +
        `(${readyCount} report-ready), no single property is set as the recommendation yet.`,
    );
  }

  for (const c of list) {
    const ph = c.status
      ? STATUS_PHRASE[c.status as Exclude<VerdictStatus, ''>]
      : 'not yet assessed';
    const mark = c.isWinner ? '★ ' : '· ';
    const reason = (c.reasoning || '').trim();
    lines.push(`${mark}${c.label.trim()} — ${ph}${reason ? `: ${reason}` : ''}`);
  }

  return lines.join('\n');
}
